/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import React, { useEffect, useRef, useState } from 'react';
import { IEditServiceProps, NewTaskData, SubTaskFormState } from './ModifyServices.Model';
import { Box, Divider, Typography } from '@mui/material';
import theme from '../../../../../Shared/Themes/theme';
import { BoxShadow } from '../../../../../Shared/Constants/Style.Constants';
import { PersonName } from '../AllJobs.Style';
import { buttonStyles } from '../../../../Jobs/Components/UpdateShiftDate/UpdateShiftDate.Style';
import { useTranslation } from 'react-i18next';
import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';
import Modal from '../../../../Jobs/Utilities/Shared/Components/Modal';
import ModifyPopUp from '../../../../Jobs/Components/JobDetails/ModifyPopUp';
import { CreateTaskSubtaskMappingDto } from '../../../../../Shared/Models';
import { DEFAULT_PAGE_NUMBER, ServiceCreationType, SUBTASK_TYPE } from '../../../../../Shared/Constants/App';
import { styles } from './ModifyServices.Style';
import AddSubTaskModal from './AddSubTaskModal';
import TaskList from './TaskList';
import AvailableTasksList from './AvailableTasksList';
import { useModifyServicesContext } from './ModifyServicesContext';

const EditService: React.FC<IEditServiceProps> = (props) => {
  const {
    servicesData,
    expandedTask,
    filteredAvailableTasks,
    subTask,
    taskOptions,
    isTasksLoading,
    totalCount,
    currentPage,
    searchString,
    isServiceTaskLoading,
    setOpenTaskId,
    setIsEditService,
    setAddNewTask,
    setSearchString,
    setSubTask,
    setServiceTaskMapping,
    handleToggle,
    handleSubTaskChanges,
    handlePaginationChange,
    handleCreateTask,
    handleServiceTaskMapping,
    subTasksObj,
    handleTaskSubTaskMapping,
    isServicesLoading,
    isTaskSubTaskMapLoading,
    isTaskCreationLoading,
    selectedServiceId,
    setSelectedServiceId,
  } = props;

  const { setExpandedTask, setCurrentPage } = useModifyServicesContext();

  const [addSubTask, setAddSubTask] = useState(false);
  const [createNewSubTask, setCreateNewSubTask] = useState(false);
  const [subTaskForm, setSubTaskForm] = useState<SubTaskFormState>({
    name: '',
    description: '',
    type: '',
  });
  const [taskForSubTask, setTaskForSubTask] = useState<string | null>(null);
  const [isConfirmButtonLoading, setIsConfirmButtonLoading] = useState(false);
  const [isConfirmChanges, setIsConfirmChanges] = useState(false);
  const [addTaskToService, setAddTaskToService] = useState(false);

  const { divider } = buttonStyles;
  const { t } = useTranslation(['taskManagement']);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const selectedService =
    servicesData?.find((s: any) => s.mdServiceId === selectedServiceId) || servicesData?.[0] || {};

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenTaskId(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setOpenTaskId]);

  useEffect(() => {
    function handleClickOutsideExpandedTask(event: any) {
      const clickedElement = event.target as HTMLElement;
      const isClickInsideTaskContainer = clickedElement.closest('[data-task-container]');

      if (!isClickInsideTaskContainer && expandedTask) {
        setExpandedTask(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutsideExpandedTask);
    return () => document.removeEventListener('mousedown', handleClickOutsideExpandedTask);
  }, [expandedTask, setExpandedTask]);

  useEffect(() => {
    setExpandedTask(null);
  }, [selectedServiceId, setExpandedTask]);

  const handleSubTaskTypeChange = (event: { target: { value: string } }) => {
    setSubTaskForm((prev) => ({ ...prev, type: event.target.value }));
  };

  const updateSubTaskForm = (field: keyof SubTaskFormState, value: string) => {
    setSubTaskForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetSubTaskForm = () => {
    setSubTaskForm({ name: '', description: '', type: '' });
    setSubTask('');
    setCreateNewSubTask(false);
    setSearchString('');
    setCurrentPage(DEFAULT_PAGE_NUMBER);
  };

  const resetAllModals = () => {
    setAddSubTask(false);
    setIsEditService(false);
    setServiceTaskMapping(undefined);
    setAddTaskToService(false);
    setSelectedServiceId(null);
    setCurrentPage(DEFAULT_PAGE_NUMBER);
    resetSubTaskForm();
  };

  const addSubTaskToMapping = (subTaskData: { mdTaskId?: string; mdTaskName: string; mdTaskDescription: string }) => {
    setServiceTaskMapping((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        type: ServiceCreationType.SCHEDULE_LEVEL,
        tasks: prev.tasks?.map((task) =>
          task.mdTaskId === taskForSubTask ? { ...task, subTasks: [...task.subTasks, subTaskData] } : task
        ),
      };
    });
  };

  const createTaskSubTaskPayload = (
    taskId: string,
    taskDetails: any,
    subTaskData: { mdTaskId?: string; mdTaskName: string; mdTaskDescription: string }
  ): CreateTaskSubtaskMappingDto => ({
    tasks: [
      {
        mdTaskId: taskId,
        mdTaskName: taskDetails?.taskName || '',
        mdTaskDescription: taskDetails?.taskDescription || '',
        subTasks: [
          {
            mdTaskId: subTaskData.mdTaskId || '',
            mdTaskName: subTaskData.mdTaskName,
            mdTaskDescription: subTaskData.mdTaskDescription,
          },
        ],
      },
    ],
  });

  const handleConfirmExistingSubTask = () => {
    const newSubTask = {
      mdTaskId: subTasksObj?.mdTaskId === subTask ? subTask : undefined,
      mdTaskName: subTasksObj?.mdTaskId === subTask ? subTasksObj?.taskName : '',
      mdTaskDescription: subTasksObj?.mdTaskId === subTask ? subTasksObj?.taskDescription : '',
    };

    const taskDetails = taskOptions?.find((t) => t.value === subTask)?.tasks;
    const payload = createTaskSubTaskPayload(taskForSubTask || '', taskDetails, newSubTask);

    addSubTaskToMapping(newSubTask);

    if (subTaskForm.type === SUBTASK_TYPE.PERMANENT) {
      handleTaskSubTaskMapping(payload);
    } else {
      setIsConfirmChanges(true);
      setIsConfirmButtonLoading(false);
    }
  };

  useEffect(() => {
    if (
      !isTaskSubTaskMapLoading &&
      !isServiceTaskLoading &&
      isConfirmButtonLoading &&
      subTaskForm.type === SUBTASK_TYPE.PERMANENT
    ) {
      setIsConfirmChanges(true);
      setIsConfirmButtonLoading(false);
    }
  }, [isTaskSubTaskMapLoading]);

  const handleConfirmNewSubTask = async () => {
    const response = await handleCreateTask(subTaskForm.name, subTaskForm.description);
    if (!response) return;

    const newSubTask = {
      mdTaskId: response?.mdTaskId,
      mdTaskName: subTaskForm.name,
      mdTaskDescription: subTaskForm.description,
    };

    const taskDetails = taskOptions?.find((t) => t.value === taskForSubTask)?.tasks;
    const payload = createTaskSubTaskPayload(taskForSubTask || '', taskDetails, newSubTask);

    addSubTaskToMapping(newSubTask);

    if (subTaskForm.type === SUBTASK_TYPE.PERMANENT) {
      handleTaskSubTaskMapping(payload);
    } else {
      setIsConfirmChanges(true);
      setIsConfirmButtonLoading(false);
    }
  };

  const handleConfirmButton = () => {
    setIsConfirmButtonLoading(true);

    if (!createNewSubTask) {
      handleConfirmExistingSubTask();
    } else {
      handleConfirmNewSubTask();
    }
  };

  const createNewTaskObject = (task: any): NewTaskData => ({
    mdTaskId: task.mdTaskId || task.taskId,
    mdTaskName: task.mdTaskName || task.taskName,
    mdTaskDescription: task.mdTaskDescription || task.taskDescription || '',
    isProofOfCompletionRequired: task.isProofOfCompletionRequired ?? false,
    isInspectionRequired: task.isInspectionRequired ?? false,
    inspectionEntity: task.inspectionEntity ?? '',
    subTasks: [],
  });

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const addTaskToServiceMapping = (task: any) => {
    setServiceTaskMapping((prev) => {
      const newTask = createNewTaskObject(task);

      if (!prev) {
        return {
          type: ServiceCreationType.SCHEDULE_LEVEL,
          woScheduleMasterId: selectedService?.woScheduleMasterId || '',
          mdServiceId: selectedService?.mdServiceId || '',
          mdServiceName: selectedService?.mdServiceName || '',
          mdServiceDescription: selectedService?.mdServiceDescription || '',
          serviceMeasure: selectedService?.serviceMeasure || 0,
          mdServiceUnits: selectedService?.mdServiceUnits || '',
          fromDate: selectedService?.fromDate || '',
          endDate: selectedService?.endDate || '',
          customerId: selectedService?.customerId || '',
          tasks: [newTask],
        };
      }

      return {
        ...prev,
        tasks: prev.tasks
          ? prev.tasks.some((t) => t.mdTaskId === newTask.mdTaskId)
            ? prev.tasks
            : [...prev.tasks, newTask]
          : [newTask],
      };
    });
  };

  const handleAddSubTaskClick = (task: any) => {
    setTaskForSubTask(task.mdTaskId || task.taskId);
    setAddSubTask(true);
    addTaskToServiceMapping(task);
  };

  const handleTaskCreation = () => {
    setIsConfirmChanges(true);
  };

  const renderBodyContent = () => (
    <>
      <Typography sx={styles.sectionTitle}>{t('taskManagement:AreYouSureYouWantToAddThisTaskToService')}</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', gap: 4 }}>
        <Button
          type={ButtonType.Secondary}
          label={t('taskManagement:Cancel')}
          onClick={() => setAddTaskToService(false)}
        />
        <Button type={ButtonType.Primary} label={t('taskManagement:Confirm')} onClick={handleTaskCreation} />
      </Box>
    </>
  );

  return (
    <>
      <Box
        sx={{
          borderRadius: '1rem',
          backgroundColor: theme.palette.common.white,
          boxShadow: BoxShadow.BoxShadow1,
          padding: '1.5rem',
          margin: '1.875rem 0 1.5rem 0',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Box sx={{ flex: '0 0 auto' }}>
          <PersonName>{t('taskManagement:TasksAdded')}:&nbsp;</PersonName>
        </Box>
        <Box
          sx={{
            overflowY: expandedTask ? 'visible' : 'auto',
            maxHeight: expandedTask ? 'none' : '22rem',
            paddingRight: '0.5rem',
            marginBottom: '1rem',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}>
          <TaskList
            key={selectedServiceId || 'default'}
            tasks={selectedService?.tasks || []}
            expandedTask={expandedTask}
            handleToggle={handleToggle}
            handleAddSubTaskClick={handleAddSubTaskClick}
          />
        </Box>

        <Divider sx={{ ...divider, margin: '1rem 0', flex: '0 0 auto' }} />

        <Box sx={{ flex: '0 0 auto' }}>
          <PersonName>{t('taskManagement:TasksRemaining')}:&nbsp;</PersonName>
        </Box>
        <Box
          sx={{
            overflowY: 'auto',
            maxHeight: '22rem',
            paddingRight: '0.5rem',
            marginBottom: '1rem',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}>
          <AvailableTasksList
            tasks={filteredAvailableTasks || []}
            isLoading={isServicesLoading || false}
            handleAddSubTaskClick={handleAddSubTaskClick}
          />
        </Box>

        <Box
          sx={{
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            gap: 2,
            paddingTop: '2rem',
            borderTop: '1px solid #e0e0e0',
          }}>
          <Box
            sx={{
              ...styles.addButton,
              maxWidth: '8rem',
              opacity: isServicesLoading ? '0.5' : '1',
              cursor: isServicesLoading ? 'not-allowed' : 'pointer',
            }}
            onClick={() => {
              setIsEditService(false);
              setSelectedServiceId(null);
            }}>
            <Box sx={styles.addButtonText}>{t('taskManagement:Cancel')}</Box>
          </Box>
          <Box
            sx={{
              ...styles.addButton,
              maxWidth: '10rem',
              opacity: isServicesLoading ? '0.5' : '1',
              cursor: isServicesLoading ? 'not-allowed' : 'pointer',
            }}
            onClick={() => setAddNewTask(true)}>
            <Box sx={styles.addButtonText}>{t('taskManagement:AddTask')}</Box>
          </Box>
        </Box>
      </Box>

      <AddSubTaskModal
        open={addSubTask}
        onClose={() => {
          setAddSubTask(false);
          resetSubTaskForm();
        }}
        createNewSubTask={createNewSubTask}
        setCreateNewSubTask={setCreateNewSubTask}
        subTask={subTask}
        setSubTask={setSubTask}
        subTaskForm={subTaskForm}
        updateSubTaskForm={updateSubTaskForm}
        handleSubTaskTypeChange={handleSubTaskTypeChange}
        taskOptions={taskOptions}
        handleSubTaskChanges={handleSubTaskChanges}
        isTasksLoading={isTasksLoading}
        totalCount={totalCount}
        currentPage={currentPage}
        handlePaginationChange={handlePaginationChange}
        searchString={searchString}
        setSearchString={setSearchString}
        handleConfirmButton={handleConfirmButton}
        isTaskCreationLoading={isTaskCreationLoading}
        isTaskSubTaskMapLoading={isTaskSubTaskMapLoading}
        isServiceTaskLoading={isServiceTaskLoading}
        isConfirmButtonLoading={isConfirmButtonLoading}
      />

      <Modal
        open={addTaskToService}
        onClose={() => setAddTaskToService(false)}
        renderHeader={''}
        renderBody={renderBodyContent()}
        styles={{
          '& .MuiPaper-root': {
            width: '52rem',
          },
        }}
      />

      <ModifyPopUp
        open={isConfirmChanges}
        handleClose={() => setIsConfirmChanges(false)}
        handleOnCloseJob={() => {
          handleServiceTaskMapping();
          setIsConfirmChanges(false);
          resetAllModals();
        }}
        loading={isServiceTaskLoading || isConfirmButtonLoading}
        heading={t('taskManagement:ConfirmChanges')}
        text={t('taskManagement:AreYouSureYouWantToConfirmThisChanges')}
      />
    </>
  );
};

export default EditService;
